package com.tixforgigs.common.ui.components.grid

import com.tixforgigs.common.core.enums.DeviceClass
import com.tixforgigs.common.core.theme.Styles
import com.tixforgigs.common.core.util.InputUtil.mCurrentDeviceClass
import io.kvision.core.AlignItems
import io.kvision.core.Container
import io.kvision.core.JustifyItems
import io.kvision.html.div
import io.kvision.state.bind
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.flow.asStateFlow

@DslMarker
annotation class GridDsl

@GridDsl
class GridContainerScope internal constructor(
  private val root: Container,
  content: GridContainerScope.() -> Unit
) {

  init {
    content()
  }

  /**
   * legt ein Grid Item an
   *
   * @param columns wie viele Spalten soll das Item belegen
   * @throws IllegalArgumentException wenn rows nicht zwischen 1 und 12 liegt
   */
  fun gridItem(
    columns: Int,
    startColumn: Int? = null,
    classNames: List<String>? = null,
    horizontalAlign: JustifyItems? = null,
    verticalAlign: AlignItems? = AlignItems.START,
    content: Container.() -> Unit = {},
  ): GridItem {
    require(columns in 1..12) { "gridItem(columns) erwartet einen Wert zwischen 1 und 12" }

    val itemRoot = root.div(
      className = classNames?.joinToString(" ")
    ).bind(mCurrentDeviceClass, removeChildren = false) { deviceClass ->
      val modifiedColumns = if (deviceClass == DeviceClass.MOBILE) 12 else columns
      val modifiedStartColumn = if (deviceClass == DeviceClass.MOBILE) 1 else startColumn
      addCssStyle(Styles.GridCss.gridItemStyle(modifiedColumns, modifiedStartColumn, horizontalAlign, verticalAlign))
    }

    return GridItem(itemRoot).apply {
      content(content)
    }
  }
}

@GridDsl
class GridItem internal constructor(
  private val root: Container,
) {

  fun content(
    content: Container.() -> Unit = {},
  ) {
    root.content()
  }

  /**
   * Bindet den Inhalt des Grid-Items an ein (Mutable)StateFlow.
   * Innerhalb des Blocks steht der `Container` des Items als Receiver zur Verfügung.
   */
  fun <T> bind(
    stateFlow: StateFlow<T>,
    removeChildren: Boolean = true,
    block: Container.(T) -> Unit,
  ) {
    root.bind(stateFlow, removeChildren = removeChildren) { value ->
      block(value)
    }
  }

  fun <T> bind(
    mutableStateFlow: MutableStateFlow<T>,
    removeChildren: Boolean = true,
    block: Container.(T) -> Unit,
  ) = bind(mutableStateFlow.asStateFlow(), removeChildren = removeChildren, block = block)
}

@GridDsl
class GridContainer internal constructor(
  private val root: Container,
) {

  fun content(
    content: GridContainerScope.() -> Unit = {},
  ) {
    GridContainerScope(root, content)
  }

  /**
   * Bindet den Inhalt des Grid-Containers an ein (Mutable)StateFlow.
   * Innerhalb des Blocks stehen nur `gridItem(...)` (und andere GridContainerScope-APIs) zur Verfügung.
   */
  fun <T> bind(
    stateFlow: StateFlow<T>,
    removeChildren: Boolean = true,
    block: GridContainerScope.(T) -> Unit,
  ) {
    root.bind(stateFlow, removeChildren = removeChildren) { value ->
      GridContainerScope(this) {
        block(value)
      }
    }
  }

  fun <T> bind(
    mutableStateFlow: MutableStateFlow<T>,
    removeChildren: Boolean = true,
    block: GridContainerScope.(T) -> Unit,
  ) = bind(mutableStateFlow.asStateFlow(), removeChildren = removeChildren, block = block)
}

fun Container.gridContainer(
  classNames: List<String>? = null,
  content: GridContainerScope.() -> Unit = {},
): GridContainer {
  val gridRoot = div(className = classNames?.joinToString(" "))
  gridRoot.bind(mCurrentDeviceClass, removeChildren = false) { deviceClass ->
    DeviceClass.entries.filter { it != deviceClass }.forEach {
      removeCssStyle(Styles.GridCss.gridContainerStyle(it))
    }
    addCssStyle(Styles.GridCss.gridContainerStyle(deviceClass))
  }

  return GridContainer(gridRoot).apply {
    content(content)
  }
}